import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RefreshCw } from 'lucide-react';

import MyPerformanceTab from './performance/MyPerformanceTab';
import EvaluationsTab from './performance/EvaluationsTab';
import ManagerTab from './performance/ManagerTab';
import SetupTab from './performance/SetupTab';
import AopTab from './performance/AopTab';
import AdminTab from './performance/AdminTab';
import CompanyDashboard from './performance/CompanyDashboard';
import { API_URL } from '../config';

const API = API_URL + '/performance';

const PERIODS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'annual', label: 'Annual' },
];

const PerformancePage = () => {
  const { user } = useAuth();
  const isHR = ['super_admin', 'hr_admin', 'hr_executive'].includes(user?.role);
  const authorization = getAuthHeaders().Authorization;
  const authHeaders = useMemo(() => authorization ? { Authorization: authorization } : {}, [authorization]);

  const [tab, setTab] = useState('my-performance');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [employees, setEmployees] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [hasTeam, setHasTeam] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const hdrs = { headers: authHeaders };

      // Fetch employees list (for admin tabs)
      if (isHR) {
        const eR = await fetch(`${API}/workforce`, hdrs);
        if (eR.ok) { const d = await eR.json(); setEmployees(Array.isArray(d) ? d : d.employees || []); }
      }

      // My evaluations
      const evR = await fetch(`${API}/evaluations`, hdrs);
      if (evR.ok) setEvaluations(await evR.json());

      // Check if user has a team (for manager tab)
      const teamR = await fetch(`${API}/my-team`, hdrs);
      if (teamR.ok) { const team = await teamR.json(); setHasTeam(team.length > 0); }

    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [isHR, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const showManager = hasTeam || isHR;
  const showAdmin = isHR;

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Performance Management</h1>
          <p className="text-sm text-slate-500 mt-1">AOP → KPIs → MIS — Your complete performance chain</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="my-performance" data-testid="tab-my-performance">My Performance</TabsTrigger>
          <TabsTrigger value="evaluations" data-testid="tab-evaluations">Evaluations</TabsTrigger>
          {showManager && <TabsTrigger value="manager" data-testid="tab-manager">Team Review</TabsTrigger>}
          {showAdmin && <TabsTrigger value="setup" data-testid="tab-setup">Setup</TabsTrigger>}
          {showAdmin && <TabsTrigger value="aop" data-testid="tab-aop">AOP</TabsTrigger>}
          {showAdmin && <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>}
          {showAdmin && <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>}
        </TabsList>

        <TabsContent value="my-performance">
          <MyPerformanceTab user={user} authHeaders={authHeaders} period={period} onSaved={fetchAll} />
        </TabsContent>

        <TabsContent value="evaluations">
          <EvaluationsTab user={user} evaluations={evaluations} employees={employees} isHR={isHR} authHeaders={authHeaders} onRefresh={fetchAll} />
        </TabsContent>

        {showManager && (
          <TabsContent value="manager">
            <ManagerTab authHeaders={authHeaders} />
          </TabsContent>
        )}

        {showAdmin && (
          <TabsContent value="setup">
            <SetupTab employees={employees} authHeaders={authHeaders} />
          </TabsContent>
        )}

        {showAdmin && (
          <TabsContent value="aop">
            <AopTab authHeaders={authHeaders} employees={employees} />
          </TabsContent>
        )}

        {showAdmin && (
          <TabsContent value="compliance">
            <AdminTab employees={employees} authHeaders={authHeaders} period={period} />
          </TabsContent>
        )}

        {showAdmin && (
          <TabsContent value="company">
            <CompanyDashboard period={period} authHeaders={authHeaders} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default PerformancePage;
